/**
 * pdf.js 5 calls Promise.withResolvers() inside its worker without a fallback, so on
 * browsers without it (iOS Safari < 17.4, Chrome < 119) every PDF load fails. The
 * main-thread polyfill in src/index.tsx does not reach the worker, which has its own
 * globals; this source runs first inside the worker instead.
 *
 * Kept as a string so the bundler and minifier cannot change it: it is executed
 * verbatim in the worker.
 */
export const WORKER_POLYFILL_SOURCE = `if (typeof Promise.withResolvers !== 'function') {
	Promise.withResolvers = function () {
		var resolve, reject;
		var promise = new this(function (res, rej) {
			resolve = res;
			reject = rej;
		});
		return { promise: promise, resolve: resolve, reject: reject };
	};
}`;

/**
 * Source of the module worker pdf.js starts: the polyfill, then the real pdf.js
 * worker. A failed import is rethrown outside the promise so it reaches the
 * Worker's error event, which pdf.js handles (instead of waiting forever).
 */
export const pdfWorkerWrapperSource = (workerUrl: string) =>
	`${WORKER_POLYFILL_SOURCE}
import(${JSON.stringify(workerUrl)}).catch(function (error) {
	setTimeout(function () {
		throw error;
	});
});
`;

/**
 * workerSrc for pdf.js: a same-origin blob: module wrapping the absolute URL of the
 * real worker (still emitted and served by Vite exactly as before). Falls back to
 * the plain worker URL where blob URLs are unavailable.
 */
export function createPdfWorkerSrc(workerUrl: string): string {
	if (
		typeof Blob === 'undefined' ||
		typeof URL === 'undefined' ||
		typeof URL.createObjectURL !== 'function'
	) {
		return workerUrl;
	}
	return URL.createObjectURL(
		new Blob([pdfWorkerWrapperSource(workerUrl)], {
			type: 'text/javascript',
		}),
	);
}
